import React, { useState, useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import querystring from 'query-string';
import {
  FormControlLabel,
  makeStyles,
  Switch,
  Typography
} from '@material-ui/core';
import { Button, Modal } from 'components/Base';
import withMeta from 'hoc/withMeta';
import Cases from 'components/Cases/Cases';
import api from 'api';
import { useAuth } from 'providers/AuthProvider/SessionAuthProvider';
import { Loader } from 'components/Base';
import Forms from 'components/Forms';
import { Field, Formik } from 'formik';
import { Notify } from 'utils';
import { CustomerType } from 'store/modules/Customers/types';
import Router from 'components/Router/Router';
import Fields from 'components/Fields';
import { errHelper } from 'utils';
import IntelliofloCustomerImport from 'components/IntelliofloCustomerImport/IntellifloCustomerImport';
import SignIn from 'components/SignIn/SignIn';
import Icons from 'components/Icons';
import Store from 'store/store';
import { compose } from 'ramda';
import { secondaryText } from 'utils/colors';
const { connect, selectors } = Store;

const { useHistory } = Router;

const useStyles = makeStyles({
  btn: {
    height: 'auto',
    padding: '1rem 2rem',
    marginTop: '1rem'
  },
  searchContainer: {
    width: '350px'
  },
  btnContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end'
  }
});

const CaseComponent = ({
  canSeeMultipleAdvisors,
  availableAdvisors,
  riskProfiler,
  advisor
}: any) => {
  const location = Router.useLocation() as any;
  // Check if redirect from Inteliflo
  const { io: isRedirectedFromIo } = querystring.parse(location.search);
  const [isFetchingCases, setIsFetchingCases] = useState(false);
  const [casesList, setCases] = useState({
    count: 0,
    results: []
  });
  const [allCases, setAllCases] = useState() as any;
  const [page, setPage] = useState(1);
  // Open new Case modal if redirected from IO with prefilled data
  const [isModalOpen, setIsModalOpen] = useState(!!isRedirectedFromIo);
  const history = useHistory();
  const classes = useStyles();
  const { isAuthenticated } = useAuth();
  const [isImportUserModal, setIsImportUserModal] = useState(false);
  const [importedCustomers, setImportedCustomers] = useState([
    { first_name: '', last_name: '' }
  ] as Partial<CustomerType[]>);
  const [caseName, setCaseName] = useState('');
  const [advisers, setAdvisers] = useState([] as any);
  const [adviserOptions, setAdviserOptions] = useState([] as any);
  const [showArchived, setShowArchived] = useState(false);

  // Filter cases based on the archived toggle
  const filterCases = (cases: any) => {
    if (!showArchived) {
      return cases.filter(
        (caseItem: any) => caseItem.risk_profiler == riskProfiler
      );
    }
    return cases.filter(
      (caseItem: any) => caseItem.risk_profiler != riskProfiler
    );
  };

  const mapAdvisersToOptions = (advisers: any) => {
    return advisers
      .filter((user: any) => user.access_level === 'Adviser')
      .map((user: any) => ({
        name: user.full_name,
        value: user.id
      }));
  };

  useEffect(() => {
    api.getFirmUsers().then(({ data }: any) => {
      setAdvisers(mapAdvisersToOptions(data.results));
    });
  }, []);

  useEffect(() => {
    const allAdvisersOption = { name: 'All advisers', value: 'all' };
    if (availableAdvisors?.length > 1) {
      setAdviserOptions([
        allAdvisersOption,
        ...mapAdvisersToOptions(availableAdvisors)
      ]);
      return;
    }

    setAdviserOptions([allAdvisersOption, ...advisers]);
  }, [advisers, availableAdvisors]);

  const createNewCase = async ({
    name,
    customers,
    adviser_id = null
  }: {
    name: string;
    customers: Array<CustomerType>;
    adviser_id?: number | null;
  }) => {
    if (!name || !customers) return;
    setIsModalOpen(false);
    return api
      .createCase(name, customers, adviser_id)
      .then(({ data }: any) => {
        // Clear persist data
        localStorage.removeItem('new-case');
        return history.push(`/${data.id}/risk-summary/`);
      })
      .catch(err => {
        errHelper(err);
      });
  };

  const getCaseList = (searchValue?: string, adviser_id?: number) => {
    setIsFetchingCases(true);
    api
      .getCases({
        page: searchValue ? 1 : page,
        search: searchValue,
        adviser_id
      })
      .then(({ data }: any) => {
        setAllCases(data.results); // Save all cases (including archived ones)
        setCases({
          count: data.results.length, // Total count before filtering
          results: filterCases(data.results) // Filter the cases based on the archived toggle
        });
      })
      .catch((err: any) => {
        Notify.error(err.response.data.message);
      })
      .finally(() => {
        setIsFetchingCases(false);
      });
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    getCaseList(); // Fetch cases whenever page changes or authentication changes
    // eslint-disable-next-line
  }, [page, isAuthenticated]);

  useEffect(() => {
    if (location.state && !!location.state.newCase) {
      setIsModalOpen(true);
      history.replace('/cases');
    }
    // eslint-disable-next-line
  }, [location.state]);

  useEffect(() => {
    // Whenever `archived` toggle changes, re-filter cases
    if (allCases) {
      setCases({
        count: filterCases(allCases).length, // Adjust the count based on filtered results
        results: filterCases(allCases) // Filter cases based on archived toggle
      });
    }
  }, [showArchived, allCases]); // Re-run when `archived` toggle or `allCases` changes

  const changePage = ({ newPage }: { newPage: number }) => {
    setPage(newPage + 1);
  };

  const refreshCaseList = () => {
    getCaseList();
  };

  const importCustomerFromIO = (customers: Partial<CustomerType[]>) => {
    setIsModalOpen(true);
    setImportedCustomers(customers as Partial<CustomerType[]>);
  };

  const setIoCustomerImport = () => {
    setIsImportUserModal(true);
    setIsModalOpen(false);
  };

  const closeNewCase = () => {
    setCaseName('');
    setImportedCustomers([{ first_name: '', last_name: '' }] as Partial<
      CustomerType[]
    >);
    setIsModalOpen(false);
  };

  const setCaseAndCustomer = (e: any, customers: any) => {
    setCaseName(e);
    setImportedCustomers(customers);
  };

  return (
    <div style={{ marginTop: '28px' }}>
      <Grid container spacing={1}>
        {isAuthenticated ? (
          <>
            <Modal
              isOpen={isModalOpen}
              closeModalFunc={closeNewCase}
              disableEnforceFocus={true}
            >
              <Forms.v2.NewCase
                closeModal={closeNewCase}
                onSubmit={createNewCase}
                setIsImportUserModal={setIoCustomerImport}
                importedCustomers={importedCustomers}
                setImportedCustomers={setImportedCustomers}
                caseName={caseName}
                setCaseName={(e: any, customers: any) =>
                  setCaseAndCustomer(e.target.value, customers)
                }
              />
            </Modal>

            <IntelliofloCustomerImport
              isModalOpen={isImportUserModal && isAuthenticated}
              onClose={() => setIsImportUserModal(false)}
              importFunction={importCustomerFromIO}
            />

            <Loader isLoading={false}>
              {canSeeMultipleAdvisors ? (
                <div
                  className={classes.searchContainer}
                  style={{
                    marginBottom: '1rem',
                    backgroundColor: 'white',
                    borderRadius: '25px',
                    padding: '0.25rem 0.5rem'
                  }}
                >
                  <Formik
                    initialValues={{ adviser_id: 'all' }}
                    onSubmit={() => {
                      //
                    }}
                  >
                    <Field
                      name="adviser_id"
                      component={Fields.v2.Select}
                      fullWidth
                      variant="outlined"
                      options={adviserOptions}
                      noUnderline
                      noBackground
                      onChange={(value: any) => {
                        getCaseList(undefined, value === 'all' ? null : value);
                      }}
                      placeholder="Select adviser"
                    />
                  </Formik>
                </div>
              ) : (
                <></>
              )}
              <Grid container>
                <Grid item xs={7} md={8} lg={9}>
                  <div className={classes.searchContainer}>
                    <Fields.SearchGeneric
                      label="Search by case name, client name or case ID"
                      onSearch={getCaseList}
                    />
                  </div>
                  {advisor?.organization?.is_beta && (
                    <FormControlLabel
                      control={
                        <Switch
                          value={showArchived}
                          checked={showArchived}
                          name="showArchived"
                          onChange={e => {
                            setShowArchived(e.target.checked);
                          }}
                        />
                      }
                      label={
                        <Typography variant="subtitle2">
                          View Archived Cases
                        </Typography>
                      }
                      style={{
                        flexDirection: 'row-reverse',
                        marginLeft: '1px'
                      }}
                    />
                  )}
                </Grid>
                <Grid item xs={5} md={4} lg={3}>
                  <div className={classes.btnContainer}>
                    <Button
                      style={{ marginBottom: '1rem' }}
                      onClick={() => setIsModalOpen(true)}
                    >
                      New case
                    </Button>
                  </div>
                </Grid>
              </Grid>
              <div style={{ color: secondaryText }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                    marginBottom: '0.25rem'
                  }}
                >
                  <Icons.CopyIcon width="1.8rem" />
                  <div>
                    <span style={{ fontWeight: 600 }}>Clone cases: </span>
                    Create an exact copy of this case, including all risk
                    analysis, the client's existing portfolio, and
                    recommendations. Typically used when a client has multiple
                    objectives, such as different risk profiles or time
                    horizons.
                  </div>
                </div>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '2px' }}
                >
                  <Icons.FollowOnIcon width="1.8rem" />
                  <div>
                    <span style={{ fontWeight: 600 }}>Follow-on cases: </span>
                    Carry forward the recommended portfolio from this case as
                    the new existing portfolio in a follow-on case. The
                    follow-on case starts without a recommended portfolio.
                    Typically used when conducting an annual suitability review.
                  </div>
                </div>
              </div>
              <Loader isLoading={isFetchingCases}>
                <Cases
                  casesList={casesList.results}
                  count={casesList.count}
                  page={page - 1}
                  changePage={changePage}
                  refreshCaseList={refreshCaseList}
                  advisers={advisers}
                />
              </Loader>
            </Loader>
          </>
        ) : (
          <SignIn />
        )}
      </Grid>
    </div>
  );
};

export default compose(
  connect(
    (state: any) => ({
      canSeeMultipleAdvisors:
        selectors.advisorSelectors.canSeeMultipleAdvisors(state),
      availableAdvisors: selectors.advisorSelectors.availableAdvisors(state),
      riskProfiler: selectors.firmSelectors.riskProfiler(state),
      advisor: selectors.advisorSelectors.getAdvisor(state)
    }),
    null
  ),
  withMeta
)(CaseComponent);
